import React from 'react'
import { createPortal } from 'react-dom'
import { Zap, ShieldAlert, TriangleAlert, Minus, X, Bell } from 'lucide-react'
import { useMarketStore } from '../services/store'

export const GlobalTradePopup = () => {
    const activeTradeAlert = useMarketStore((state) => state.activeTradeAlert)
    const isTradeAlertVisible = useMarketStore((state) => state.isTradeAlertVisible)
    const isTradeAlertMinimized = useMarketStore((state) => state.isTradeAlertMinimized)
    const setTradeAlertVisible = useMarketStore((state) => state.setTradeAlertVisible)
    const setTradeAlertMinimized = useMarketStore((state) => state.setTradeAlertMinimized)

    if (!isTradeAlertVisible || !activeTradeAlert) return null

    // Support for both direct setup and nested setup object format
    const alertSetup = activeTradeAlert.setup || activeTradeAlert
    const warning = activeTradeAlert.warning

    // Only render on client
    if (typeof document === 'undefined') return null

    return createPortal(
        <div
            className={`fixed bottom-0 sm:bottom-6 right-0 sm:right-6 z-9999 transition-all duration-500 ease-spring ${
                isTradeAlertMinimized
                    ? 'w-14 h-14 rounded-full translate-y-0'
                    : 'w-full sm:w-[360px] translate-y-0 opacity-100'
            }`}
        >
            <div
                className={`relative ${
                    isTradeAlertMinimized
                        ? 'w-full h-full'
                        : 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/50 dark:border-slate-700/50 shadow-2xl overflow-hidden ring-1 ring-black/5 dark:ring-white/10 rounded-2xl sm:rounded-3xl'
                }`}
            >
                {/* MINIMIZED VIEW - Premium Bell */}
                {isTradeAlertMinimized ? (
                    <button
                        onClick={() => setTradeAlertMinimized(false)}
                        className="w-14 h-14 flex items-center justify-center relative group"
                    >
                        {/* Clean White/Dark Background */}
                        <div className="absolute inset-0 rounded-full bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 group-hover:shadow-xl transition-shadow duration-200" />
                        {/* Bell Icon */}
                        <Bell
                            size={18}
                            className="relative z-10 text-slate-600 dark:text-slate-300 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors"
                        />
                        {/* Red Notification Dot */}
                        <span className="absolute top-0 right-0 w-3 h-3 bg-rose-500 rounded-full border-2 border-white dark:border-slate-800 z-20" />
                    </button>
                ) : (
                    /* EXPANDED VIEW */
                    <div className="flex flex-col">
                        {/* Header */}
                        <div
                            className={`flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-700 ${
                                alertSetup.color === 'emerald'
                                    ? 'bg-emerald-50/50 dark:bg-emerald-900/20'
                                    : alertSetup.color === 'rose'
                                      ? 'bg-rose-50/50 dark:bg-rose-900/20'
                                      : 'bg-indigo-50/50 dark:bg-indigo-900/20'
                            }`}
                        >
                            <div className="flex items-center gap-2.5">
                                <div
                                    className={`p-1.5 rounded-lg shadow-sm ${
                                        alertSetup.strategyClass === 'TRAP'
                                            ? 'bg-rose-500 text-white animate-pulse'
                                            : alertSetup.color === 'emerald'
                                              ? 'bg-emerald-500 text-white'
                                              : alertSetup.color === 'rose'
                                                ? 'bg-rose-500 text-white'
                                                : 'bg-indigo-500 text-white'
                                    }`}
                                >
                                    {alertSetup.strategyClass === 'TRAP' ? (
                                        <TriangleAlert size={14} />
                                    ) : (
                                        <Zap size={14} />
                                    )}
                                </div>
                                <div>
                                    <h5 className="text-[10px] font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider">
                                        {alertSetup.strategyClass === 'TRAP' ? 'TRAP ALERT' : 'New Setup'}
                                    </h5>
                                    <h4 className="text-sm font-black text-slate-800 dark:text-slate-100 leading-none mt-0.5">
                                        {alertSetup.header}
                                    </h4>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex flex-col items-end mr-1">
                                    <span className="text-[7px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                        PROBABILITY
                                    </span>
                                    <span
                                        className={`text-sm font-black leading-none ${
                                            alertSetup.color === 'emerald'
                                                ? 'text-emerald-500'
                                                : alertSetup.color === 'rose'
                                                  ? 'text-rose-500'
                                                  : 'text-indigo-500'
                                        }`}
                                    >
                                        {alertSetup.conviction}%
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setTradeAlertMinimized(true)}
                                        className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                                        title="Minimize"
                                    >
                                        <Minus size={16} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            // Make closing == minimizing as per user request
                                            setTradeAlertMinimized(true)
                                            // Optional: also completely hide if needed, but user said "if closed also consider minimize"
                                            // setIsPopupVisible(false)
                                        }}
                                        className="p-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:text-rose-600 dark:hover:text-rose-400 rounded-full transition-colors text-slate-400 dark:text-slate-500"
                                        title="Minimize"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-4 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                                        Action
                                    </span>
                                    <span
                                        className={`text-xl font-black ${
                                            alertSetup.color === 'emerald'
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : alertSetup.color === 'rose'
                                                  ? 'text-rose-600 dark:text-rose-400'
                                                  : 'text-indigo-600 dark:text-indigo-400'
                                        }`}
                                    >
                                        {alertSetup.type}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md premium-cell-mono">
                                        {alertSetup.strike}
                                    </div>
                                </div>
                            </div>

                            {/* WARNING BANNER IN POPUP */}
                            {warning && (
                                <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg animate-pulse-slow">
                                    <ShieldAlert size={14} className="text-amber-500 mt-0.5 shrink-0" />
                                    <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 leading-tight">
                                        {warning}
                                    </span>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase block mb-0.5">
                                        Entry
                                    </span>
                                    <span className="font-bold text-slate-700 dark:text-slate-300">
                                        {alertSetup.entry}
                                    </span>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase block mb-0.5">
                                        Stop
                                    </span>
                                    <span className="font-bold text-rose-600 dark:text-rose-400">
                                        {alertSetup.sl}
                                    </span>
                                </div>
                                <div className="col-span-2 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700 flex justify-between items-center">
                                    <div>
                                        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase block mb-0.5">
                                            Target
                                        </span>
                                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                            {alertSetup.target}
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-bold text-emerald-500 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                                        {alertSetup.upside}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="p-1 w-full bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700">
                            <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-slate-400/30 w-full animate-progress-indeterminate" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    )
}
